import React, { useMemo } from 'react';
// redux
import { useAppSelector } from '../../app/hooks';
import { useTheme } from '@mui/material/styles';
// interface
import { Badge, Company } from '../../interfaces/users.interface';
import { Survey } from '../../interfaces/survey.interface';
import { AssessmentSurvey, AssessorAnswer } from '../../interfaces/assessment.interface';
// services
import { MILESTONE_STATUS, assessmentDisplayLogic, getRemediationCounts, surveyDisplayLogic } from '../../services/surveyHelpers';
// components
import { AssessmentSurveyAuditLogs } from '../Assessments/AssessmentAuditLog';
import { Workflow } from '../../interfaces/workflow.interface';
import { Typography } from '@mui/material';
import { USER_TYPES } from '../../services/user';

type WorkflowStatusProps = {
  workflow?: Workflow;
  company: Company;
  survey?: Survey;
  assessmentSurvey?: AssessmentSurvey;
  hasBlueExpired?: boolean;
  hasGoldExpired?: boolean;
  hideBallCounts?: boolean;
};

export function getWorkflowStatusForStep({
  workflow,
  company,
  survey,
  assessmentSurvey,
}: {
  workflow?: Workflow;
  company: Company;
  survey?: Survey;
  assessmentSurvey?: AssessmentSurvey;
}): string {
  const surveyStatusDisplay = surveyDisplayLogic({
    survey,
    companyCerts: company.companyCertifications,
    workflow,
  });

  // If JSX.Element(ie. Progress Bar) return default in-progress status
  const surveyDisplayLogicString = typeof surveyStatusDisplay === 'string' ? surveyStatusDisplay : MILESTONE_STATUS.QUESTIONNAIRE_IN_PROGRESS;

  const remediationDisplayStatus = () => {
    if (!assessmentSurvey) return '';
    const status = (assessmentSurvey.assessorAnswers as AssessorAnswer[]).some(it => ['remediation'].indexOf(it?.status || '') > -1 && !it.vendorStatus)
      ? MILESTONE_STATUS.ASSESSMENT_PENDING_REMEDIATION_PLANS
      : MILESTONE_STATUS.REMEDIATION_PLANS_SUBMITTED;
    return status;
  };

  const hasRemediationCounts = Boolean(workflow?.status === 'Remediation' || (workflow?.status === 'Complete' && assessmentSurvey?.bestPracticeRemediationCount));

  return !workflow?.assessmentSurvey
    ? surveyDisplayLogicString
    : hasRemediationCounts
      ? remediationDisplayStatus()
      : assessmentDisplayLogic(workflow.status, (workflow.badges ?? []) as Badge[]);
}

const WorkflowStatus = (props: WorkflowStatusProps) => {
  const theme = useTheme();
  const { workflow, company, survey, assessmentSurvey, hasBlueExpired, hasGoldExpired, hideBallCounts = false } = props;
  // redux
  const { user } = useAppSelector((state) => state.user);
  const surveyStatusDisplay = useMemo(() => surveyDisplayLogic({
    survey,
    companyCerts: company.companyCertifications,
    workflow,
    hideProgressBar: hideBallCounts,
  }), [survey, company, workflow]);

  const showRemediationCounts = useMemo(() => {
    return workflow?.status === 'Remediation' || (workflow?.status === 'Complete' && assessmentSurvey?.bestPracticeRemediationCount) && user?.type !== USER_TYPES.ASSESSOR;
  }, [workflow?.status, assessmentSurvey?.bestPracticeRemediationCount, user?.type]);

  const remediationDisplayStatus = useMemo(() => {
    if (!assessmentSurvey) return '';
    const status = (assessmentSurvey.assessorAnswers as AssessorAnswer[]).some(it => ['remediation'].indexOf(it?.status || '') > -1 && !it.vendorStatus)
      ? MILESTONE_STATUS.ASSESSMENT_PENDING_REMEDIATION_PLANS
      : MILESTONE_STATUS.REMEDIATION_PLANS_SUBMITTED;
    return status;
  }, [assessmentSurvey]);

  const displayStatus = useMemo(() => {
    if (workflow?.assessmentSurvey) {
      if (showRemediationCounts) {
        return remediationDisplayStatus;
      }
      return assessmentDisplayLogic(workflow?.status, workflow.badges as Badge[]);
    }
    return surveyStatusDisplay;
  }, [workflow, showRemediationCounts, remediationDisplayStatus, surveyStatusDisplay]);

  return (
    <>
      {!!workflow?.assessmentSurvey && workflow?.isActive && (
        <AssessmentSurveyAuditLogs assessmentSurveyId={workflow.assessmentSurvey} status={workflow.status} />
      )}
      <Typography sx={{ color: theme.palette.customThemeColors?.darkGrey }}>
        {displayStatus}
      </Typography>
      {Boolean(workflow?.assessmentSurvey && showRemediationCounts && !hideBallCounts) &&
        getRemediationCounts({ assessmentSurvey })
      }
      {hasGoldExpired && (
        <Typography sx={{ color: theme.palette.customThemeColors?.darkGrey }}>Assessment Expired</Typography>
      )}
      {!hasGoldExpired && hasBlueExpired && (
        <Typography sx={{ color: theme.palette.customThemeColors?.darkGrey }}>Questionnaire Expired</Typography>
      )}
    </>
  );
};

export default WorkflowStatus;
